import { useMemo, useState, type RefObject } from 'react';

import { useResizeObserver } from '../../core/hooks/useResizeObserver.js';
import {
  resolvePanelSizing,
  type PanelSizing,
  type PanelSizingConstraints,
} from './panel-sizing.js';
import { usePanelDrag, type PanelDragActions } from './usePanelDrag.js';
import { type PanelWidth } from './width-utils.js';

interface UseSplitSizingOptions {
  panel: {
    min: PanelWidth;
    max: PanelWidth;
    default: PanelWidth;
    position: 'start' | 'end';
    resizable: boolean;
    onResize?: (width: number) => void;
    width?: number;
  };
  content: {
    min: PanelWidth;
  };
  refs: {
    root: RefObject<HTMLDivElement | null>;
    panel: RefObject<HTMLDivElement | null>;
    content: RefObject<HTMLDivElement | null>;
  };
}

interface SplitSizing extends PanelSizing {
  dragActions: PanelDragActions;
}

export function useSplitSizing(options: UseSplitSizingOptions): SplitSizing {
  const { panel, content, refs } = options;

  const [containerWidth, setContainerWidth] = useState(0);
  // Unset until the user drags, so a percentage default keeps re-resolving
  // against the container while the panel is still following the default.
  const [draggedWidth, setDraggedWidth] = useState<number | undefined>(
    undefined,
  );

  const constraints = useMemo<PanelSizingConstraints>(
    () => ({
      minWidth: panel.min,
      maxWidth: panel.max,
      reservedMin: content.min,
      resizable: panel.resizable,
    }),
    [panel.min, panel.max, content.min, panel.resizable],
  );

  useResizeObserver({
    // Unfortunately, the `useResizeObserver` hook doesn't support generic null types
    // so we have to cast here to avoid a type error.
    ref: refs.root as RefObject<Element>,
    onResize: (size) => {
      if (!size.width) {
        return;
      }
      setContainerWidth(size.width);
    },
  });

  const sizing = resolvePanelSizing(
    {
      controlledWidth: panel.width,
      draggedWidth,
      defaultWidth: panel.default,
    },
    containerWidth,
    constraints,
  );

  const dragActions = usePanelDrag({
    containerRef: refs.root,
    constraints,
    panelWidthPx: sizing.panel.width,
    direction: panel.position === 'start' ? 1 : -1,
    paint: (dragged) => {
      if (refs.panel.current) {
        refs.panel.current.style.width = dragged.panel.widthCss;
      }
      if (refs.content.current) {
        refs.content.current.style.width = dragged.content.widthCss;
      }
    },
    commit: (widthPx) => {
      setDraggedWidth(widthPx);
      panel.onResize?.(widthPx);
    },
  });

  return { ...sizing, dragActions };
}
